import type { MarkerPoint } from "../../hooks/map/useMarkers";

export type VictimMapFilter = {
  statuses: string[];
  genders: string[];
  ageMin: number | null;
  ageMax: number | null;
  dateFrom: string | null;
  dateTo: string | null;
};

export const DEFAULT_VICTIM_MAP_FILTER: VictimMapFilter = {
  statuses: [],
  genders: [],
  ageMin: null,
  ageMax: null,
  dateFrom: null,
  dateTo: null,
};

function normalize(value: unknown): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function matchesDate(dateOfCrime: unknown, dateFrom: string | null, dateTo: string | null) {
  if (!dateFrom && !dateTo) return true;
  if (typeof dateOfCrime !== "string") return false;

  const value = dateOfCrime.slice(0, 10);
  if (!value) return false;

  if (dateFrom && value < dateFrom) return false;
  if (dateTo && value > dateTo) return false;
  return true;
}

export function applyVictimMapFilter(
  markers: MarkerPoint[],
  filter: VictimMapFilter,
): MarkerPoint[] {
  const statusSet = new Set(filter.statuses.map((s) => normalize(s)).filter(Boolean));
  const genderSet = new Set(filter.genders.map((g) => normalize(g)).filter(Boolean));

  return markers.filter((marker) => {
    if (marker.kind === "custom") return true;

    const payload = marker.payload as Record<string, unknown> | undefined;
    if (!payload) return false;

    const status = normalize(payload.status);
    const gender = normalize(payload.gender);
    const age = typeof payload.age === "number" ? payload.age : null;

    if (statusSet.size > 0 && !statusSet.has(status)) return false;
    if (genderSet.size > 0 && !genderSet.has(gender)) return false;

    if (filter.ageMin != null && (age == null || age < filter.ageMin)) return false;
    if (filter.ageMax != null && (age == null || age > filter.ageMax)) return false;

    if (!matchesDate(payload.dateOfCrime, filter.dateFrom, filter.dateTo)) return false;

    return true;
  });
}

export function countActiveVictimMapFilters(filter: VictimMapFilter): number {
  let count = 0;
  if (filter.statuses.length > 0) count += 1;
  if (filter.genders.length > 0) count += 1;
  if (filter.ageMin != null || filter.ageMax != null) count += 1;
  if (filter.dateFrom || filter.dateTo) count += 1;
  return count;
}